import { useMemo, useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import YomaIcon from "../components/YomaIcon";
import { ICONS } from '../components/yomaIcons'

const GOLD     = "#C9A96E";
const INK      = "#1C1915";
const INK_SOFT = "#4A4540";
const BORDER   = "#E7E1D8";
const SOFT_BG  = "#F7F5F2";
const WHITE    = "#FFFFFF";

const inputStyle = {
  width: "100%", padding: "9px 11px",
  border: `1px solid ${BORDER}`, borderRadius: 7,
  fontSize: 13, color: INK, background: "#FAFAF8",
  boxSizing: "border-box", outline: "none",
};
const labelStyle = {
  display: "block", fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", color: INK_SOFT, marginBottom: 4, fontWeight: 600,
};
const sectionTitle = {
  fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase",
  color: GOLD, marginBottom: 14, fontWeight: 700,
};
const card = {
  background: WHITE, border: `1px solid ${BORDER}`,
  borderRadius: 12, padding: "22px 24px", marginBottom: 16,
};

const FORM_INIT = {
  nomClient: "", typeClient: "", budget: "", zone: "",
  typeProjet: "", delai: "", terrainExistant: "",
  niveauDecision: "", urgence: "", notes: "",
};

function toNumber(v) { return Number(v || 0); }

function evaluer(f) {
  let score = 0;
  const details = [];

  if (toNumber(f.budget) >= 3000000)     { score += 3; details.push({ label: "Budget > 3M MAD",        pts: 3, ok: true }); }
  else if (toNumber(f.budget) >= 1500000){ score += 2; details.push({ label: "Budget 1.5M–3M MAD",     pts: 2, ok: true }); }
  else if (toNumber(f.budget) > 0)       { score += 1; details.push({ label: "Budget < 1.5M MAD",      pts: 1, ok: true }); }
  else                                    {             details.push({ label: "Budget non renseigné",   pts: 0, ok: false }); }

  if (f.terrainExistant === "oui")        { score += 2; details.push({ label: "Terrain existant",       pts: 2, ok: true }); }
  else if (f.terrainExistant === "non")   {             details.push({ label: "Pas de terrain",         pts: 0, ok: false }); }

  if (f.niveauDecision === "decideur")    { score += 3; details.push({ label: "Décideur direct",        pts: 3, ok: true }); }
  else if (f.niveauDecision === "influenceur") { score += 1; details.push({ label: "Influenceur",       pts: 1, ok: true }); }
  else if (f.niveauDecision === "exploratoire") {           details.push({ label: "Contact exploratoire", pts: 0, ok: false }); }

  if (f.urgence === "haute")              { score += 2; details.push({ label: "Urgence haute",          pts: 2, ok: true }); }
  else if (f.urgence === "moyenne")       { score += 1; details.push({ label: "Urgence moyenne",        pts: 1, ok: true }); }

  if (f.delai === "0-3")                  { score += 2; details.push({ label: "Délai 0–3 mois",         pts: 2, ok: true }); }
  else if (f.delai === "3-6")             { score += 1; details.push({ label: "Délai 3–6 mois",         pts: 1, ok: true }); }
  else if (f.delai)                       {             details.push({ label: "Délai > 6 mois",         pts: 0, ok: false }); }

  if (f.typeProjet)                       { score += 1; details.push({ label: "Type projet défini",     pts: 1, ok: true }); }
  if (f.zone)                             { score += 1; details.push({ label: "Zone définie",           pts: 1, ok: true }); }
  if (f.typeClient)                       { score += 1; details.push({ label: "Profil client défini",   pts: 1, ok: true }); }

  const max = 15;
  const pct = Math.round((score / max) * 100);

  let maturite, priorite, couleur, recommandation, action;
  if (score >= 10) {
    maturite = "Élevée"; priorite = "Haute"; couleur = "#1A7A44";
    recommandation = "Lead qualifié — proposer rapidement un rendez-vous stratégique et lancer l'analyse de faisabilité.";
    action = "Basculer vers Analyse Projet";
  } else if (score >= 6) {
    maturite = "Moyenne"; priorite = "Moyenne"; couleur = "#B7770D";
    recommandation = "Lead intéressant — compléter les informations manquantes, recontacter sous 48h pour affiner le projet.";
    action = "Recontacter et compléter";
  } else {
    maturite = "Faible"; priorite = "Basse"; couleur = "#C0392B";
    recommandation = "Lead peu mature — collecter davantage d'informations avant d'investir du temps commercial.";
    action = "Mettre en veille";
  }

  return { score, max, pct, maturite, priorite, couleur, recommandation, action, details };
}

export default function AcquisitionClientPage({ cabinetId = null }) {
  const [form,     setForm]     = useState(FORM_INIT);
  const [loading,  setLoading]  = useState(false);
  const [msg,      setMsg]      = useState({ text: "", type: "success" });
  const [saved,    setSaved]    = useState([]);
  const [vueListe, setVueListe] = useState(false);

  const eval_ = useMemo(() => evaluer(form), [form]);

  useEffect(() => { if (cabinetId == null) return; fetchSaved(); }, [cabinetId]);

  async function fetchSaved() {
    const { data } = await supabase
      .from("prospects")
      .select("*")
      .eq("cabinet_id", cabinetId)
      .order("created_at", { ascending: false })
      .limit(10);
    if (data) setSaved(data);
  }

  function upd(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function showMsg(text, type = "success") {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "success" }), 4000);
  }

  async function handleSauvegarder() {
    if (!form.nomClient) { showMsg("Le nom du client est requis.", "error"); return; }
    setLoading(true);
    const [prenom, ...rest] = form.nomClient.trim().split(" ");
    const nom = rest.join(" ") || prenom;

    const { error } = await supabase.from("prospects").insert({
      cabinet_id: cabinetId,
      prenom, nom,
      budget_min: toNumber(form.budget),
      zone_souhaitee: form.zone,
      type_projet: form.typeProjet,
      delai: form.delai,
      notes: form.notes,
      statut: eval_.priorite === "Haute" ? "qualifie" : "nouveau",
      score: eval_.score,
    });

    if (error) { showMsg("Erreur : " + error.message, "error"); }
    else {
      await supabase.from("kanban_cards").insert({
        cabinet_id: cabinetId,
        titre: `${form.nomClient} — ${form.typeProjet || "Prospect"}`,
        description: `Budget : ${form.budget ? Number(form.budget).toLocaleString("fr-FR") + " MAD" : "?"} · Zone : ${form.zone || "—"}\nScore : ${eval_.score}/${eval_.max} · Priorité : ${eval_.priorite}`,
        colonne: "prospection",
        swimlane: "neuf",
        priorite: eval_.priorite === "Haute" ? "urgent" : eval_.priorite === "Moyenne" ? "attente" : "normal",
      });

      // ✓ Sauvegarde pour DecisionPage
      localStorage.setItem("yoma_decision_prospect", JSON.stringify({
        nomProspect:  form.nomClient,
        typeProjet:   form.typeProjet,
        delaiRaw:     form.delai,
        scorePrequal: eval_.score,
      }));

      showMsg(`Lead "${form.nomClient}" sauvegardé et ajouté au Kanban ✓`);
      await fetchSaved();
      setTimeout(() => setForm(FORM_INIT), 1500);
    }
    setLoading(false);
  }

  const badgeStyle = (couleur) => ({
    background: couleur + "18", color: couleur,
    border: `1px solid ${couleur}40`,
    borderRadius: 999, padding: "5px 14px",
    fontSize: 11, fontWeight: 700,
  });

  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100 }}><div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}><div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "18px" }}><YomaIcon>{ICONS.clipboard}</YomaIcon><div><h1 style={{ margin: 0, fontSize: "28px", fontWeight: 500, fontFamily: "Playfair Display" }}>
              Acquisition Client
            </h1><p style={{ margin: "4px 0 0", color: "#8A7B6D", fontSize: "14px", fontFamily: "DM Sans" }}>
              Qualification, suivi et transformation des prospects
            </p></div></div><button onClick={() => setVueListe(v => !v)}
          style={{ padding: "8px 18px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {vueListe ? "▲ Masquer historique" : ` Historique (${saved.length})`}
        </button></div>

      {vueListe && saved.length > 0 && (
        <div style={{ ...card, marginBottom: 20 }}><div style={sectionTitle}>Derniers leads qualifiés</div><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {saved.map(p => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 14px", background: SOFT_BG, borderRadius: 8 }}><div style={{ width: 36, height: 36, borderRadius: "50%", background: GOLD, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, color: WHITE }}>
                  {(p.prenom || "?")[0].toUpperCase()}
                </div><div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700 }}>{p.prenom} {p.nom}</div><div style={{ fontSize: 11, color: INK_SOFT }}>{p.type_projet || "—"} · {p.zone_souhaitee || "—"}</div></div><span style={{ fontSize: 10, fontWeight: 700, background: p.statut === "qualifie" ? "#E8F6EF" : "#EEF4FD", color: p.statut === "qualifie" ? "#1A7A44" : "#2E6DB4", borderRadius: 999, padding: "3px 10px" }}>
                  {p.statut}
                </span><div style={{ fontSize: 11, color: INK_SOFT }}>
                  {new Date(p.created_at).toLocaleDateString("fr-FR")}
                </div></div>
            ))}
          </div></div>
      )}

      {msg.text && (
        <div style={{ marginBottom: 16, padding: "10px 16px", background: msg.type === "error" ? "#FDEDEC" : "#E8F6EF", borderRadius: 8, fontSize: 13, color: msg.type === "error" ? "#C0392B" : "#1A7A44", fontWeight: 600 }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 20, alignItems: "start" }}><div><div style={card}><div style={sectionTitle}>Identification</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div style={{ gridColumn: "1 / -1" }}><label style={labelStyle}>Nom du client *</label><input style={inputStyle} value={form.nomClient} onChange={e => upd("nomClient", e.target.value)} placeholder="Ex : Karim Benali" /></div><div><label style={labelStyle}>Type de client</label><select style={inputStyle} value={form.typeClient} onChange={e => upd("typeClient", e.target.value)}><option value="">— Sélectionner —</option><option value="particulier">Particulier</option><option value="investisseur">Investisseur</option><option value="promoteur">Promoteur</option><option value="mre">MRE</option></select></div><div><label style={labelStyle}>Niveau de décision</label><select style={inputStyle} value={form.niveauDecision} onChange={e => upd("niveauDecision", e.target.value)}><option value="">— Sélectionner —</option><option value="decideur">Décideur direct</option><option value="influenceur">Influenceur</option><option value="exploratoire">Exploratoire</option></select></div></div></div><div style={card}><div style={sectionTitle}>Projet</div><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}><div><label style={labelStyle}>Type de projet</label><select style={inputStyle} value={form.typeProjet} onChange={e => upd("typeProjet", e.target.value)}><option value="">— Sélectionner —</option><option value="Villa neuve">Villa neuve</option><option value="Rénovation">Rénovation</option><option value="Riad">Riad</option><option value="Lotissement">Lotissement</option><option value="Immeuble">Immeuble</option><option value="Terrain + construction">Terrain + construction</option></select></div><div><label style={labelStyle}>Zone souhaitée</label><select style={inputStyle} value={form.zone} onChange={e => upd("zone", e.target.value)}><option value="">— Sélectionner —</option><option value="Marrakech - Palmeraie">Marrakech — Palmeraie</option><option value="Marrakech - Route de Fès">Marrakech — Route de Fès</option><option value="Marrakech - Route d'Amizmiz">Marrakech — Route d'Amizmiz</option><option value="Marrakech - Guéliz">Marrakech — Guéliz</option><option value="Marrakech - Médina">Marrakech — Médina</option><option value="Agadir">Agadir</option><option value="Essaouira">Essaouira</option><option value="Autre">Autre</option></select></div><div><label style={labelStyle}>Budget estimé (MAD)</label><input style={inputStyle} type="number" value={form.budget} onChange={e => upd("budget", e.target.value)} placeholder="Ex : 2 500 000" /></div><div><label style={labelStyle}>Terrain existant ?</label><select style={inputStyle} value={form.terrainExistant} onChange={e => upd("terrainExistant", e.target.value)}><option value="">— Sélectionner —</option><option value="oui">Oui</option><option value="non">Non</option></select></div><div><label style={labelStyle}>Délai souhaité</label><select style={inputStyle} value={form.delai} onChange={e => upd("delai", e.target.value)}><option value="">— Sélectionner —</option><option value="0-3">0 à 3 mois</option><option value="3-6">3 à 6 mois</option><option value="6-12">6 à 12 mois</option><option value="12+">Plus de 12 mois</option></select></div><div><label style={labelStyle}>Urgence</label><select style={inputStyle} value={form.urgence} onChange={e => upd("urgence", e.target.value)}><option value="">— Sélectionner —</option><option value="haute">Haute</option><option value="moyenne">Moyenne</option><option value="faible">Faible</option></select></div></div></div><div style={card}><div style={sectionTitle}>Notes internes</div><textarea style={{ ...inputStyle, resize: "vertical", minHeight: 90, fontFamily: "inherit" }}
              value={form.notes} onChange={e => upd("notes", e.target.value)}
              placeholder="Contexte, freins, points d'attention, source du lead..." /></div><div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}><button onClick={() => setForm(FORM_INIT)}
              style={{ padding: "10px 20px", background: SOFT_BG, color: INK_SOFT, border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 13, cursor: "pointer" }}>
              Réinitialiser
            </button><button onClick={handleSauvegarder} disabled={loading || !form.nomClient}
              style={{ padding: "10px 24px", background: GOLD, color: WHITE, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: !form.nomClient ? 0.5 : 1 }}>
              {loading ? "..." : " Sauvegarder + Kanban"}
            </button></div></div><div><div style={{ ...card, borderTop: `4px solid ${eval_.couleur}` }}><div style={sectionTitle}>Évaluation automatique</div><div style={{ marginBottom: 16 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}><span style={{ fontSize: 13, fontWeight: 600 }}>Score de qualification</span><span style={{ fontSize: 22, fontWeight: 800, color: eval_.couleur }}>{eval_.score}/{eval_.max}</span></div><div style={{ height: 10, background: SOFT_BG, borderRadius: 999, overflow: "hidden" }}><div style={{ height: "100%", width: `${eval_.pct}%`, background: eval_.couleur, borderRadius: 999, transition: "width 0.4s" }} /></div><div style={{ fontSize: 10, color: INK_SOFT, marginTop: 4, textAlign: "right" }}>{eval_.pct}%</div></div><div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}><span style={badgeStyle(eval_.couleur)}>Maturité : {eval_.maturite}</span><span style={badgeStyle(eval_.couleur)}>Priorité : {eval_.priorite}</span></div><div style={{ background: SOFT_BG, borderRadius: 8, padding: "12px 14px", borderLeft: `3px solid ${eval_.couleur}` }}><div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: eval_.couleur, marginBottom: 6 }}>
                Recommandation YOMA
              </div><div style={{ fontSize: 13, color: INK, lineHeight: 1.6 }}>{eval_.recommandation}</div></div><div style={{ marginTop: 12, padding: "10px 14px", background: eval_.couleur + "15", borderRadius: 8, display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 16 }}>→</span><span style={{ fontSize: 12, fontWeight: 700, color: eval_.couleur }}>{eval_.action}</span></div></div><div style={card}><div style={sectionTitle}>Détail du scoring</div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {eval_.details.map((d, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 10px", background: d.ok ? "#E8F6EF" : SOFT_BG, borderRadius: 7, border: `1px solid ${d.ok ? "#B2DEC8" : BORDER}` }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 13 }}>{d.ok ? "✓" : "○"}</span><span style={{ fontSize: 12, color: d.ok ? INK : INK_SOFT }}>{d.label}</span></div><span style={{ fontSize: 11, fontWeight: 700, color: d.ok ? "#1A7A44" : "#bbb" }}>
                    +{d.pts} pt{d.pts > 1 ? "s" : ""}
                  </span></div>
              ))}
            </div></div><div style={{ ...card, background: SOFT_BG }}><div style={sectionTitle}>Lecture métier YOMA</div><div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                "Un terrain existant augmente fortement la maturité du lead.",
                "Un décideur direct est prioritaire sur un contact exploratoire.",
                "Budget clair + délai court = lead à traiter en priorité.",
                "Score ≥ 10 : basculer vers l'analyse de faisabilité.",
                "Score 6–9 : recontacter sous 48h pour compléter.",
                "Score < 6 : mettre en veille ou nurturer.",
              ].map((t, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12, color: INK_SOFT }}><span style={{ color: GOLD, fontWeight: 700, flexShrink: 0 }}>›</span><span>{t}</span></div>
              ))}
            </div></div></div></div></div>
  );
}
